import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import logo from '../../Assets/Logos/b_logo_white.svg';
import slogan from '../../Assets/Logos/baemingo_logo_white.svg';
import GetBack from '~Scenes/SelfServiceKiosks/Components/GetBack';
import { useStorage } from '~Components/Storage';
import { useLogger, useSelector } from '~Hooks';
import Onboarding from '~Scenes/Onboarding';
import NumberInput from '~ComponentsImproved/Numpad';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { gql, useQuery } from '@apollo/client';
import { TablesForSimplePAT } from './__queries__';
import { BackIcon } from '~Scenes/Handy/styles';
import { usePage } from '~Scenes/Handy/Context/PageProvider';
import Heading from '~ComponentsImproved/Typography/Heading';

const Wrapper = styled.div`
  width: 100vw;
  background: #000;
  height: calc(100vh - 60px);
  padding-top: 160px;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const Button = styled.div<{
  disabled?: boolean;
}>`
  color: #333;
  background: #fff;
  position: absolute;
  bottom: 30px;
  left: 30px;
  right: 30px;
  padding: 25px;
  font-size: 25px;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
`;

const GoBack = styled.div`
  display: flex;
  align-items: center;

  position: absolute;
  top: 30px;
  left: 30px;
`;

const Logo = styled.div`
  height: 136px;
  mask-image: url(${logo});
  width: 138px;
  mask-size: contain;
  margin: 20px auto;
  background-color: #fff;
  mask-repeat: no-repeat;
  mask-position: center;
`;

const Slogan = styled.div`
  height: 76px;
  margin: 0 auto;
  mask-image: url(${slogan});
  width: 188px;
  mask-size: contain;
  background-color: #fff;
  mask-position: center;
  mask-repeat: no-repeat;
`;

const TABLES_QUERY = gql`
  query TablesForSimplePAT($id: ID!) {
    venue: Venue(id: $id) {
      id
      name

      tables {
        id
        code
      }
    }
  }
`;

const SimplePAT = () => {
  const logger = useLogger('SimplePAT');
  const initialTableDataLoaded = !!useSelector((state) => state.lastTableEventData);
  const POSMenuLoaded = Boolean(useSelector((state) => state.POSMenu));

  const universe = useTableUniverse();

  const api = useTableApi();

  const [showInput, setShowInput] = useState(false);
  const [storageData] = useStorage();
  const venueId = useSelector((state) => state.venueId);
  const hasStaff = useSelector((state) => (state.companyStaff?.staffs.length ?? 0) > 0);
  const [loading, setLoading] = useState(true);

  const paymentMethods = useSelector((state) => state.POSPaymentMethods);

  const tablesQuery = useQuery<TablesForSimplePAT>(TABLES_QUERY, {
    skip: !venueId,
    variables: {
      id: venueId,
    },
  });

  // Get all open tables - based on the open cheques
  const tables = useMemo(() => {
    return universe.cheques.reduce(
      (arr, cheque) => {
        // If the cheque is at a table and we havent already added this table to our array of tables
        if (cheque.tableId && !arr.some((t) => t.id === cheque.tableId)) {
          // Find the table in the data from backend
          const table = tablesQuery?.data?.venue?.tables.find((t) => t.id === cheque.tableId);

          // This can be false in the scenario that our backend query fails. Should probably be handled in a better way
          if (table) {
            arr.push({
              id: cheque.tableId,
              code: table.code,
            });
          }
        }
        return arr;
      },
      [] as Array<{
        id: string;
        code: string;
      }>,
    );
  }, [universe.cheques, tablesQuery?.data?.venue?.tables]);

  // Wait for venueId before loading <ReceiptPrinter> and <Onboarding>
  useEffect(() => {
    if (!venueId || !loading) {
      return;
    }

    setLoading(false);
  }, [venueId]);

  const isZettle = paymentMethods.find((p) => p.id === 'IZETTLE');

  const buttonIsDisabled = tablesQuery.loading || !initialTableDataLoaded || !POSMenuLoaded;

  // Used in handy to navigate back to home
  // const page = usePage();

  return (
    <Wrapper>
      {/* <GoBack onClick={() => page.navigateTo('home')}>
        <BackIcon />
        <Heading level={4} color="#fff">
          Home
        </Heading>
      </GoBack> */}
      <GetBack />
      <Logo />
      <Slogan />
      {showInput && (
        <NumberInput
          onClose={() => {
            setShowInput(false);
          }}
          onSubmit={(value) => {
            setShowInput(false);

            logger.info(`Flow started (${value})`);

            if (value === '' || value === '0') {
              return;
            }

            /**
             * If its Zettle, dont use regular Pay at table which is very adyen-focused.
             * Instead, find the first open cheque at the table and initiate
             * payment on that
             */
            if (isZettle) {
              const table = tables.find((t) => t.code === value);

              if (!table) {
                logger.warn('Could not find table: ' + value);
                return;
              }

              const firstCheque = universe.cheques.find((c) => c.tableId === table.id);

              if (!firstCheque) {
                logger.warn('Could not find cheque for table: ' + table.code);
                return;
              }

              logger.info(`Initiating Zettle payment for table ${table.code}`);

              api.initiatePayment(firstCheque.id, table.code, 'IZETTLE');
            } else {
              logger.info(`Initiating Adyen Pay at table for table ${value}`);
              // @ts-expect-error
              window.payAtTable(value);
            }
          }}
          title={hasStaff && !isZettle ? 'Enter staff code' : 'Enter table number'}
          submitText="Submit"
          decimal
        />
      )}

      <Button disabled={buttonIsDisabled} onTouchStart={() => setShowInput(true)}>
        {buttonIsDisabled ? 'Loading' : 'Pay Table'}
      </Button>
      {!loading && <Onboarding />}
    </Wrapper>
  );
};

export default SimplePAT;
